// Card #1652 (design §6.1) — originally the 7 mode types collapsed into 3
// layout families. Card #1797 added a 4th family, BLANK, for the pare-down's
// Blank mode type: unlike the other three, it needs no composed `fields` at
// all (see ScoreboardContent.jsx, which special-cases this family BEFORE
// the "no fields yet" empty-state gate the other three families share).
// Card #1795 added a 4th, MESSAGE, for the same reason as BLANK — its one
// piece of config (the message text) lives directly on the mode, never in
// `fields` (see MODE_TYPES.MESSAGE's header comment in score-common) — but
// unlike BLANK, an EMPTY message is not "on purpose": boardRenderState.js's
// classifyBoardRenderState special-cases MESSAGE separately from BLANK so a
// mode with no configured text still falls back to Blank (card #1795 hard
// requirement 3), rather than reusing BLANK's unconditional "always OK"
// bypass.
// This is the single map deciding which family a modeType renders through;
// ScoreboardContent.jsx is the only call site.
//
// Card #1794 (2026-08-20 pare-down) removed types 1, 2, 4, 6
// (CLASS_SUMMARY_DETAILED, CLASS_SUMMARY_SIMPLE, MID_RIDE_SIMPLE,
// MID_CLASS_RESULT) from MODE_TYPE entirely (cards #2483/#2484 later re-added
// MID_RIDE_SIMPLE and MID_CLASS_RESULT as brand-new types; the two Class
// Summary types stay removed). Both of the SUMMARY family's
// members (1, 2) are gone, so LAYOUT_FAMILY.SUMMARY is removed here too —
// per AC4, a family with no surviving type is exactly as orphaned as the
// per-type compose functions and layout components (SummaryLayout.jsx,
// StartList.jsx, both deleted) that only served it. A stored view row
// naming a removed modeType (a kebab-case string like
// "class-summary-detailed") simply isn't a key in FAMILY_BY_MODE_TYPE any
// more, so the lookup returns `undefined` and `layoutFamilyForModeType`
// falls back to `null` (its existing fails-closed contract, unchanged by
// this card) — ScoreboardContent routes a null family through
// classifyBoardRenderState's UNKNOWN_MODE case to BlankLayout, never a
// crash (#1798).
import { MODE_TYPE } from "./modeTypes";

export const LAYOUT_FAMILY = {
  SINGLE_ENTRY: "single-entry",
  RANKED_LIST: "ranked-list",
  BLANK: "blank",
  MESSAGE: "message",
  // Card #2485 -- one configured picture (+ optional caption), non-data-driven.
  IMAGE: "image",
  // Card #2483 -- During/Mid-Ride [Simple]: the ride in the ring right now.
  // Its own family (not SINGLE_ENTRY) so SingleEntryLayout, which owns the
  // frozen RIDE_INTRO_DETAILED rendering, is never touched.
  MID_RIDE_SIMPLE: "mid-ride-simple",
  // Card #3098 -- Class Summary (spec types 1 and 2): ONE family for the one
  // `class-summary` mode type, whose two field sets (detailed start list,
  // simple facts grid) are picked by the mode's `fieldSet`, not by a second
  // family or a second mode type.
  CLASS_SUMMARY: "class-summary",
};

const FAMILY_BY_MODE_TYPE = {
  [MODE_TYPE.RIDE_INTRO_DETAILED]: LAYOUT_FAMILY.SINGLE_ENTRY,
  [MODE_TYPE.POST_RIDE_SCORE]: LAYOUT_FAMILY.SINGLE_ENTRY,
  [MODE_TYPE.END_OF_CLASS_RESULT]: LAYOUT_FAMILY.RANKED_LIST,
  // Card #2484 -- Mid-Class Result REUSES the ranked-list family (one layout,
  // one by-division rotation timer, one resolveResultLayout): only the wording
  // differs, and that is the rankMode ScoreboardContent derives from modeType.
  [MODE_TYPE.MID_CLASS_RESULT]: LAYOUT_FAMILY.RANKED_LIST,
  [MODE_TYPE.MESSAGE]: LAYOUT_FAMILY.MESSAGE,
  [MODE_TYPE.BLANK]: LAYOUT_FAMILY.BLANK,
  [MODE_TYPE.IMAGE]: LAYOUT_FAMILY.IMAGE,
  [MODE_TYPE.MID_RIDE_SIMPLE]: LAYOUT_FAMILY.MID_RIDE_SIMPLE,
  [MODE_TYPE.CLASS_SUMMARY]: LAYOUT_FAMILY.CLASS_SUMMARY,
};

// Fails closed like modeTypes.js's other gates: an unrecognized/absent
// modeType resolves to no family at all, never a default render.
export function layoutFamilyForModeType(modeType) {
  return FAMILY_BY_MODE_TYPE[modeType] || null;
}
